/**
 * src/config/lifecycleConstants.js
 *
 * Centralized constants for the question lifecycle and review-status workflows.
 * Import from this file instead of hardcoding strings in components.
 *
 * ── Model overview ────────────────────────────────────────────────────────────
 *
 *  status (question AVAILABILITY — drives learner-facing exam eligibility)
 *    new     — newly imported; awaiting SME approval before going live
 *    active  — approved and available in learner-facing exam sessions
 *    retired — removed from learner-facing use
 *
 *  reviewStatus (HUMAN REVIEW TRACKING — independent of availability)
 *    unreviewed — no human review has been completed yet     (default for imports)
 *    reported   — flagged by a learner or SME for investigation
 *    in_review  — under active investigation
 *    reviewed   — human review completed (corrected OR accepted as-is)
 *    removed    — quarantined; hidden from the editor and excluded from exams
 *
 * ── Workflow ──────────────────────────────────────────────────────────────────
 *
 *  Bulk import    → status: new,    reviewStatus: unreviewed
 *  Approve        → status: active, reviewStatus: reviewed   (set together)
 *  Flag issue     → reviewStatus: reported
 *  Investigate    → reviewStatus: in_review
 *  Close review   → reviewStatus: reviewed
 *  Remove         → status: retired, reviewStatus: removed   (quarantined)
 *  Retire         → status: retired (reviewStatus unchanged)
 *
 * ── Approval rule ─────────────────────────────────────────────────────────────
 *
 *  Approving a question (new → active) automatically sets reviewStatus to
 *  'reviewed', so active questions are normally also reviewed questions.
 *  If a question is later flagged, reviewStatus moves to 'reported' → 'in_review'
 *  → 'reviewed' independently of status.
 *
 * ── Exam session rule ─────────────────────────────────────────────────────────
 *
 *  Only questions with status === 'active' appear in learner-facing sessions.
 *  status 'new' and 'retired' are always excluded.
 *  reviewStatus has NO effect on session eligibility — a reported or in_review
 *  question that is already active may still surface in sessions (the admin
 *  can retire it if it should be removed while under investigation).
 */

// ── Question lifecycle (status field) ─────────────────────────────────────────

/** All valid values for question.status (must match DB CHECK constraint). */
export const QUESTION_STATUSES = ['new', 'active', 'retired'];

/** Human-readable labels for display. */
export const QUESTION_STATUS_LABELS = {
  new:     'New',
  active:  'Active',
  retired: 'Retired',
};

/** Badge colour config per status. */
export const QUESTION_STATUS_COLORS = {
  new:     { color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
  active:  { color: '#2e7d32', bg: '#f1f8f1', border: '#a5d6a7' },
  retired: { color: '#777',    bg: '#f5f5f5', border: '#bdbdbd' },
};

// ── Review / issue-tracking status (reviewStatus field) ───────────────────────

/** All valid values for question.reviewStatus (must match DB CHECK constraint). */
export const REVIEW_STATUSES = ['unreviewed', 'reported', 'in_review', 'reviewed', 'removed'];

/** Human-readable labels for display. */
export const REVIEW_STATUS_LABELS = {
  unreviewed: 'Unreviewed',
  reported:   'Reported',
  in_review:  'In Review',
  reviewed:   'Reviewed',
  removed:    'Removed',
};

/** Badge colour config per review status. */
export const REVIEW_STATUS_COLORS = {
  unreviewed: { color: '#777',    bg: '#f5f5f5', border: '#bdbdbd' },
  reported:   { color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
  in_review:  { color: '#e07b00', bg: '#fff8e1', border: '#ffe082' },
  reviewed:   { color: '#2e7d32', bg: '#f1f8f1', border: '#a5d6a7' },
  removed:    { color: '#616161', bg: '#eeeeee', border: '#9e9e9e' },
};
